import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Trophy, Medal, Award, Crown, Flame, Users,
  ChevronUp, ChevronDown, Minus
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
        <Crown className="w-5 h-5 text-white" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center shadow-lg">
        <Medal className="w-5 h-5 text-white" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center shadow-lg">
        <Award className="w-5 h-5 text-white" />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-gray-300">
      {rank}
    </div>
  );
};

const LeaderboardRow = ({ entry, theme, index }) => {
  const isTopThree = entry.rank <= 3;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`
        flex items-center gap-4 p-3 rounded-xl transition-all
        ${entry.is_current_user
          ? theme === 'cosmic'
            ? 'bg-cyan-500/20 border-2 border-cyan-500/50'
            : 'bg-amber-100 border-2 border-amber-400'
          : isTopThree
            ? theme === 'cosmic'
              ? 'bg-gradient-to-r from-slate-800/80 to-purple-900/30 border border-purple-500/30'
              : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
            : theme === 'cosmic'
              ? 'bg-slate-800/50 hover:bg-slate-800/70'
              : 'bg-gray-50 hover:bg-gray-100'
        }
      `}
    >
      {/* Rank */}
      <RankBadge rank={entry.rank} />
      
      {/* Player Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-semibold truncate ${
            entry.is_current_user
              ? theme === 'cosmic' ? 'text-cyan-300' : 'text-amber-700'
              : theme === 'cosmic' ? 'text-white' : 'text-gray-900'
          }`}>
            {entry.name}
          </span>
          {entry.is_current_user && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              theme === 'cosmic' ? 'bg-cyan-500/30 text-cyan-300' : 'bg-amber-200 text-amber-700'
            }`}>
              You
            </span>
          )}
        </div>
        <div className={`text-xs ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
          {entry.total_games} games played
        </div>
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-4">
        {/* Win Rate */}
        <div className="text-right">
          <div className={`text-lg font-bold ${
            entry.win_rate >= 60 ? 'text-green-400' :
            entry.win_rate >= 40 ? theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700' :
            'text-red-400'
          }`}>
            {entry.win_rate}%
          </div>
          <div className={`text-xs ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
            {entry.wins} wins
          </div>
        </div>
        
        {/* Streak */}
        {entry.best_streak > 0 && (
          <div className={`
            flex items-center gap-1 px-2 py-1 rounded-lg
            ${theme === 'cosmic' ? 'bg-orange-500/20' : 'bg-orange-100'}
          `}>
            <Flame className="w-4 h-4 text-orange-500" />
            <span className={`text-sm font-medium ${theme === 'cosmic' ? 'text-orange-400' : 'text-orange-600'}`}>
              {entry.best_streak}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const LeaderboardDialog = ({ isOpen, onClose, theme }) => {
  const { isAuthenticated } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadLeaderboard();
    }
  }, [isOpen]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/leaderboard`, {
        withCredentials: true
      });
      setLeaderboard(data);
      
      // Get my rank if authenticated
      if (isAuthenticated) {
        try {
          const { data: rankData } = await axios.get(`${API_URL}/api/leaderboard/my-rank`, {
            withCredentials: true
          });
          setMyRank(rankData);
        } catch (e) {
          setMyRank(null);
        }
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`
        max-w-lg max-h-[85vh] overflow-hidden
        ${theme === 'cosmic'
          ? 'bg-slate-900 border-slate-700 text-white'
          : 'bg-white'
        }
      `}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className={theme === 'cosmic' ? 'text-yellow-400' : 'text-amber-600'} />
            Global Leaderboard
          </DialogTitle>
        </DialogHeader>

        {/* My Rank Banner */}
        {myRank && myRank.rank && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`
              p-4 rounded-xl mb-2
              ${theme === 'cosmic'
                ? 'bg-gradient-to-r from-cyan-900/50 to-purple-900/50 border border-cyan-500/30'
                : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
              }
            `}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Your Rank
                </div>
                <div className={`text-3xl font-bold ${theme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'}`}>
                  #{myRank.rank}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-2xl font-bold ${myRank.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                  {myRank.win_rate}%
                </div>
                <div className={`text-sm ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {myRank.wins}/{myRank.total_games} wins
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {myRank && !myRank.rank && (
          <div className={`
            p-3 rounded-lg text-center text-sm
            ${theme === 'cosmic' ? 'bg-slate-800 text-gray-400' : 'bg-gray-100 text-gray-600'}
          `}>
            Play at least 5 games to appear on the leaderboard
          </div>
        )}

        {/* Leaderboard List */}
        <div className="overflow-y-auto max-h-[50vh] pr-2 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Users className={`w-16 h-16 mx-auto mb-4 ${theme === 'cosmic' ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                No players yet
              </p>
              <p className={`text-sm ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                Be the first to play 5 games!
              </p>
            </div>
          ) : (
            leaderboard.map((entry, idx) => (
              <LeaderboardRow
                key={entry.rank}
                entry={entry}
                theme={theme}
                index={idx}
              />
            ))
          )}
        </div>

        {/* Info */}
        <div className={`
          mt-2 p-3 rounded-lg text-xs
          ${theme === 'cosmic' ? 'bg-slate-800/50 text-gray-500' : 'bg-gray-50 text-gray-500'}
        `}>
          <p>🏆 Rankings based on win rate (minimum 5 games)</p>
        </div>

        {/* Close button */}
        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className={theme === 'cosmic' ? 'border-slate-600' : ''}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeaderboardDialog;
